import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAiCredits } from './useAiCredits';

interface ItemWithImages {
  id: string;
  title: string;
  images?: Array<{ url: string }>;
}

/**
 * Hook that automatically generates images for items that don't have any.
 * Only generates one image at a time to avoid rate limits.
 * Respects AI credit limits - won't generate if user has no credits.
 */
export function useAutoGenerateImages(items: ItemWithImages[] | undefined) {
  const queryClient = useQueryClient();
  const generatingRef = useRef<Set<string>>(new Set());
  const hasTriedRef = useRef<Set<string>>(new Set());

  // Get AI credits status
  const { hasCredits, consumeCredit, isLoading: creditsLoading } = useAiCredits();

  const generateMutation = useMutation({
    mutationFn: (itemId: string) => api.items.generateImage(itemId),
    onSuccess: (_, itemId) => {
      // Invalidate to refresh the items list
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['aiCredits'] });
      generatingRef.current.delete(itemId);
    },
    onError: (_, itemId) => {
      generatingRef.current.delete(itemId);
    },
  });

  useEffect(() => {
    if (!items || items.length === 0) return;

    // Don't auto-generate if credits are still loading or user has no credits
    if (creditsLoading || !hasCredits) return;

    // Find items without images that we haven't tried yet
    const itemsWithoutImages = items.filter(
      item =>
        (!item.images || item.images.length === 0) &&
        !generatingRef.current.has(item.id) &&
        !hasTriedRef.current.has(item.id)
    );

    // Generate image for the first item without one
    const firstItem = itemsWithoutImages[0];
    if (firstItem && !generateMutation.isPending) {
      generatingRef.current.add(firstItem.id);
      hasTriedRef.current.add(firstItem.id);

      // Consume credit before generating
      consumeCredit().then(({ success }) => {
        if (success) {
          console.log(`Auto-generating image for: ${firstItem.title}`);
          generateMutation.mutate(firstItem.id);
        } else {
          // No credits available, don't generate
          generatingRef.current.delete(firstItem.id);
        }
      });
    }
  }, [items, generateMutation.isPending, hasCredits, creditsLoading, consumeCredit]);

  return {
    isGenerating: generateMutation.isPending,
    generatingCount: generatingRef.current.size,
  };
}
